import { 
  signInWithRedirect,
  signInWithPopup,
  getRedirectResult,
  signOut, 
  onAuthStateChanged, 
  User,
  UserCredential,
  setPersistence,
  browserLocalPersistence
} from 'firebase/auth';
import { auth, googleProvider } from '../firebase';

let redirectResultPromise: Promise<UserCredential | null> | null = null;

const popupClosedButSessionMayExist = (code?: string, message?: string): boolean => {
  return code === 'auth/popup-closed-by-user'
    || code === 'auth/cancelled-popup-request'
    || (message?.includes('Cross-Origin-Opener-Policy') ?? false);
};

// Popup 在 COOP 下常回報已關閉，登入結果卻還沒寫回這個網址。整頁重整讀不到該工作階段，改走 redirect。
const recoverPopupSignIn = async (code?: string, message?: string): Promise<boolean> => {
  const signedInUser = await waitForCurrentUser(3000);
  if (signedInUser) {
    return true;
  }

  if (!popupClosedButSessionMayExist(code, message)) {
    return false;
  }

  sessionStorage.setItem('pendingRedirect', 'true');
  await signInWithRedirect(auth, googleProvider);
  return true;
};

const waitForCurrentUser = (timeoutMs: number): Promise<User | null> => {
  if (auth.currentUser) {
    return Promise.resolve(auth.currentUser);
  }

  return new Promise((resolve) => {
    let settled = false;
    const finish = (user: User | null) => {
      if (settled) {
        return;
      }
      settled = true;
      clearTimeout(timeoutId);
      unsubscribe();
      resolve(user);
    };

    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        finish(user);
      }
    });

    const timeoutId = setTimeout(() => {
      finish(auth.currentUser);
    }, timeoutMs);

    auth.authStateReady()
      .then(() => {
        if (auth.currentUser) {
          finish(auth.currentUser);
        }
      })
      .catch(() => {
        // 改由 listener 或逾時收斂
      });
  });
};

// 使用 Google SSO 登入（推薦使用 Popup 方式，避免重定向帶來的跨域問題）
export const signInWithGoogle = async (usePopup: boolean = true): Promise<void | UserCredential> => {
  try {
    // 確保認證持久性設置為 LOCAL
    try {
      await setPersistence(auth, browserLocalPersistence);
    } catch {
      // 忽略持久性設置錯誤（可能已經設置）
    }

    if (usePopup) {
      // 使用 Popup 登入
      try {
        const result = await signInWithPopup(auth, googleProvider);
        return result;
      } catch (popupError: unknown) {
        const popupFirebaseError = popupError as { code?: string; message?: string };

        if (popupFirebaseError.code === 'auth/popup-blocked') {
          // eslint-disable-next-line no-console
          console.warn('⚠️ Popup 被阻擋，改用 Redirect 登入');
          sessionStorage.setItem('pendingRedirect', 'true');
          await signInWithRedirect(auth, googleProvider);
          return;
        }

        const recovered = await recoverPopupSignIn(
          popupFirebaseError.code,
          popupFirebaseError.message
        );
        if (recovered) {
          return;
        }

        throw popupError;
      }
    } else {
      // 使用 Redirect 登入（保留作為備選，但在 localhost 可能會有問題）
      // 設置待處理重定向標記
      sessionStorage.setItem('pendingRedirect', 'true');
      await signInWithRedirect(auth, googleProvider);
    }
  } catch (error: unknown) {
    if (!usePopup) {
      sessionStorage.removeItem('pendingRedirect');
    }
    
    const firebaseError = error as { code?: string; message?: string };
    // eslint-disable-next-line no-console
    console.error(`❌ ${usePopup ? 'Popup' : 'Redirect'} 登入失敗:`, {
      code: firebaseError?.code,
      message: firebaseError?.message,
      error: firebaseError,
      stack: (error as Error)?.stack
    });
    throw error;
  }
};

const readRedirectResult = async (): Promise<UserCredential | null> => {
  try {
    // 檢查 URL 參數中是否有錯誤信息
    const urlParams = new URLSearchParams(window.location.search);
    const errorParam = urlParams.get('error');
    const errorDescription = urlParams.get('error_description');
    
    if (errorParam) {
      // eslint-disable-next-line no-console
      console.error('❌ URL 中包含錯誤參數:', {
        error: errorParam,
        description: errorDescription,
        fullUrl: window.location.href
      });
    }

    // 檢查是否有 Firebase 錯誤在 hash 中
    const hashParams = new URLSearchParams(window.location.hash.substring(1));
    const hashError = hashParams.get('error');
    if (hashError) {
      // eslint-disable-next-line no-console
      console.error('❌ Hash 中包含錯誤:', hashError);
    }

    const result = await getRedirectResult(auth);
    
    if (result) {
      // 清除待處理重定向標記
      sessionStorage.removeItem('pendingRedirect');
    } else {
      // 清除待處理重定向標記（如果存在）
      sessionStorage.removeItem('pendingRedirect');
    }
    return result;
  } catch (error: unknown) {
    const firebaseError = error as { code?: string; message?: string };
    
    // 在 catch 區塊中重新獲取 URL 參數（因為 errorParam 在 try 區塊中定義）
    const urlParams = new URLSearchParams(window.location.search);
    const errorParam = urlParams.get('error');
    
    // eslint-disable-next-line no-console
    console.error('❌ 處理重定向結果失敗:', {
      code: firebaseError?.code,
      message: firebaseError?.message,
      error: firebaseError,
      url: window.location.href,
      urlErrorParam: errorParam
    });
    
    // 提供更詳細的錯誤診斷訊息
    if (firebaseError?.code === 'auth/api-key-not-valid') {
      // eslint-disable-next-line no-console
      console.error('🔴 API Key 無效！請檢查：');
      // eslint-disable-next-line no-console
      console.error('1. OAuth 同意畫面設置（最重要）');
      // eslint-disable-next-line no-console
      console.error('   - 如果應用程式處於測試模式，確保您的 Google 帳號在測試用戶列表中');
      // eslint-disable-next-line no-console
      console.error('   - 前往: https://console.cloud.google.com/apis/credentials/consent?project=crm-teleworker');
      // eslint-disable-next-line no-console
      console.error('2. 授權域名設置');
      // eslint-disable-next-line no-console
      console.error('   - OAuth 同意畫面：確保包含 localhost');
      // eslint-disable-next-line no-console
      console.error('   - Firebase Authentication：確保包含 localhost');
      // eslint-disable-next-line no-console
      console.error('   - 前往: https://console.firebase.google.com/project/crm-teleworker/authentication/settings');
      // eslint-disable-next-line no-console
      console.error('3. OAuth Client ID 設置');
      // eslint-disable-next-line no-console
      console.error('   - 確保 http://localhost:3000 在 Authorized JavaScript origins 中');
      // eslint-disable-next-line no-console
      console.error('   - 前往: https://console.cloud.google.com/apis/credentials?project=crm-teleworker');
      // eslint-disable-next-line no-console
      console.error('📖 詳細指南: 查看 .cursor/LOCALHOST_SSO_TROUBLESHOOTING.md');
    } else if (firebaseError?.code === 'auth/unauthorized-domain') {
      // eslint-disable-next-line no-console
      console.error('🔴 未授權的網域！請檢查：');
      // eslint-disable-next-line no-console
      console.error('1. Firebase Authentication 授權域名');
      // eslint-disable-next-line no-console
      console.error('   - 確保 localhost 在授權域名列表中');
      // eslint-disable-next-line no-console
      console.error('   - 前往: https://console.firebase.google.com/project/crm-teleworker/authentication/settings');
      // eslint-disable-next-line no-console
      console.error('2. OAuth Client ID 授權來源');
      // eslint-disable-next-line no-console
      console.error('   - 確保 http://localhost:3000 在 Authorized JavaScript origins 中');
      // eslint-disable-next-line no-console
      console.error('   - 前往: https://console.cloud.google.com/apis/credentials?project=crm-teleworker');
      // eslint-disable-next-line no-console
      console.error('📖 詳細指南: 查看 .cursor/LOCALHOST_SSO_TROUBLESHOOTING.md');
    } else if (errorParam === 'access_denied') {
      // eslint-disable-next-line no-console
      console.error('🔴 訪問被拒絕！可能的原因：');
      // eslint-disable-next-line no-console
      console.error('1. 應用程式處於測試模式，但您的 Google 帳號不在測試用戶列表中');
      // eslint-disable-next-line no-console
      console.error('   - 前往: https://console.cloud.google.com/apis/credentials/consent?project=crm-teleworker');
      // eslint-disable-next-line no-console
      console.error('   - 點擊 Test users 標籤，添加您的 Google 帳號');
      // eslint-disable-next-line no-console
      console.error('2. 用戶取消了登入流程');
      // eslint-disable-next-line no-console
      console.error('📖 詳細指南: 查看 .cursor/LOCALHOST_SSO_TROUBLESHOOTING.md');
    }
    return null;
  }
};

// 處理重定向結果（在應用啟動時調用）。Strict Mode 會重跑 effect，結果只能讀一次。
export const handleRedirectResult = (): Promise<UserCredential | null> => {
  if (!redirectResultPromise) {
    redirectResultPromise = readRedirectResult();
  }
  return redirectResultPromise;
};

// 登出
export const signOutUser = async (): Promise<void> => {
  return signOut(auth);
};

// 監聽身份驗證狀態變化
export const onAuthStateChange = (callback: (user: User | null) => void) => {
  return onAuthStateChanged(auth, callback);
};

// 獲取當前用戶
export const getCurrentUser = (): User | null => {
  return auth.currentUser;
};

// 強制刷新 token（用於解決 token 過期問題）
export const refreshToken = async (): Promise<string | null> => {
  try {
    const user = auth.currentUser;
    if (!user) {
      return null;
    }
    // 強制刷新 token
    const token = await user.getIdToken(true);
    return token;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('刷新 Token 失敗:', error);
    return null;
  }
};

// 檢查用戶是否已登入
export const isUserLoggedIn = (): boolean => {
  return auth.currentUser !== null;
}; 